import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { Dashboard } from '@/pages/Dashboard';
import { RecruiterIntelligence } from '@/pages/RecruiterIntelligence';
import { InvestorIntelligence } from '@/pages/InvestorIntelligence';
import { FundingIntelligence } from '@/pages/FundingIntelligence';
import { EcommerceIntelligence } from '@/pages/EcommerceIntelligence';
import { ContactSocials } from '@/pages/ContactSocials';
import { BookingAIgent } from '@/pages/BookingAIgent';
import { MyListsContacts } from '@/pages/MyListsContacts';
import { MyListsCompanies } from '@/pages/MyListsCompanies';
import { MyListsPodcasts } from '@/pages/MyListsPodcasts';
import { MyListsHot } from '@/pages/MyListsHot';
import { Research } from '@/pages/Research';
import { CompanySearch } from '@/pages/CompanySearch';
import { CustomKpiSearch } from '@/pages/CustomKpiSearch';
import { Scrappy } from '@/pages/Scrappy';
import { PaymentResults } from '@/pages/PaymentResults';
import { PaymentConfirmation } from '@/pages/PaymentConfirmation';
import { PaymentCheckout } from '@/pages/PaymentCheckout';
import { SetPassword } from '@/pages/SetPassword';
import { Login } from '@/pages/Login';
import { Searches } from '@/pages/Searches';
import { TestDrive } from '@/pages/TestDrive';
import { TestDriveReport } from '@/pages/TestDriveReport';
import { AIEngagementProtocol } from '@/pages/AIEngagementProtocol';
import { AffiliateMarketing } from '@/pages/AffiliateMarketing';
import { CommandCenter } from '@/pages/CommandCenter';
import { AdData } from '@/pages/AdData';
import { AccountSettings } from '@/pages/AccountSettings';
import { EmailData } from '@/pages/EmailData';
import { EmailCampaignBuilder } from '@/pages/EmailCampaignBuilder';
import { EmailCampaignEditor } from '@/pages/EmailCampaignEditor';
import { EmailBuilder } from '@/pages/EmailBuilder';
import { GuideBot } from '@/components';
import { ScrapeStatusBar } from '@/components/ScrapeStatusBar';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  const isAdmin = useAppStore((state) => state.isAdmin);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/home" replace />;
  return <>{children}</>;
}

function CommandCenterRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  const isAdmin = useAppStore((state) => state.isAdmin);
  const userEmail = useAppStore((state) => state.userEmail);
  const normalizedEmail = userEmail.trim().toLowerCase();
  const isProfitibull = /@(profitibull|profitibill)\.com$/.test(normalizedEmail);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isAdmin && !isProfitibull) return <Navigate to="/home" replace />;
  return <>{children}</>;
}

function AccessRoute({ children, allowed }: { children: React.ReactNode; allowed: boolean }) {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!allowed) return <Navigate to="/home" replace />;
  return <>{children}</>;
}

function RootRoute() {
  const isAuthenticated = useAppStore((state) => state.isAuthenticated);
  if (isAuthenticated) return <Navigate to="/home" replace />;
  return <Login />;
}

export default function App() {
  const isAdmin = useAppStore((state) => state.isAdmin);
  const accessCustomKpi = useAppStore((state) => state.accessCustomKpi);
  const accessContentScraping = useAppStore((state) => state.accessContentScraping);
  const accessProfitPod = useAppStore((state) => state.accessProfitPod);
  const accessBusinessIntel = useAppStore((state) => state.accessBusinessIntel);
  const accessEmailWorkspace = useAppStore((state) => state.accessEmailWorkspace);
  const canAccessEmailWorkspace = accessEmailWorkspace || isAdmin;

  return (
    <BrowserRouter>
      <ScrapeStatusBar />
      <GuideBot />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/test-drive" element={<TestDrive />} />
        <Route path="/test-drive/report/:token" element={<TestDriveReport />} />
        <Route path="/" element={<RootRoute />} />
        <Route
          path="/lead-search"
          element={
            <ProtectedRoute>
              <Research />
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <Searches />
            </ProtectedRoute>
          }
        />
        <Route
          path="/company-search"
          element={
            <ProtectedRoute>
              <CompanySearch />
            </ProtectedRoute>
          }
        />
        <Route
          path="/custom-kpi-search"
          element={
            <AccessRoute allowed={accessCustomKpi}>
              <CustomKpiSearch />
            </AccessRoute>
          }
        />
        <Route
          path="/scrappy"
          element={
            <ProtectedRoute>
              <Scrappy />
            </ProtectedRoute>
          }
        />
        <Route
          path="/Recruiting"
          element={
            <AccessRoute allowed={accessBusinessIntel}>
              <RecruiterIntelligence />
            </AccessRoute>
          }
        />
        <Route
          path="/Investors"
          element={
            <AccessRoute allowed={accessBusinessIntel}>
              <InvestorIntelligence />
            </AccessRoute>
          }
        />
        <Route
          path="/Capital"
          element={
            <AccessRoute allowed={accessBusinessIntel}>
              <FundingIntelligence />
            </AccessRoute>
          }
        />
        <Route
          path="/Socials"
          element={
            <AccessRoute allowed={accessBusinessIntel}>
              <EcommerceIntelligence />
            </AccessRoute>
          }
        />
        <Route
          path="/affiliate-marketing"
          element={
            <AccessRoute allowed={accessBusinessIntel}>
              <AffiliateMarketing />
            </AccessRoute>
          }
        />
        <Route
          path="/contact-socials"
          element={
            <AccessRoute allowed={accessContentScraping}>
              <ContactSocials />
            </AccessRoute>
          }
        />
        <Route
          path="/ad-data"
          element={
            <AccessRoute allowed={accessContentScraping}>
              <AdData />
            </AccessRoute>
          }
        />
        <Route
          path="/social-listener"
          element={
            <AccessRoute allowed={accessContentScraping}>
              <AdData />
            </AccessRoute>
          }
        />
        <Route
          path="/trending-keyword-research"
          element={
            <AccessRoute allowed={accessContentScraping}>
              <AdData />
            </AccessRoute>
          }
        />
        <Route
          path="/group-scraping"
          element={
            <AccessRoute allowed={accessContentScraping}>
              <AdData />
            </AccessRoute>
          }
        />
        <Route
          path="/podcasts"
          element={
            <AccessRoute allowed={accessProfitPod}>
              <BookingAIgent />
            </AccessRoute>
          }
        />
        <Route
          path="/my-lists/contacts"
          element={
            <ProtectedRoute>
              <MyListsContacts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-lists/companies"
          element={
            <ProtectedRoute>
              <MyListsCompanies />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-lists/hot"
          element={
            <ProtectedRoute>
              <MyListsHot />
            </ProtectedRoute>
          }
        />
        <Route
          path="/my-lists/podcasts"
          element={
            <ProtectedRoute>
              <MyListsPodcasts />
            </ProtectedRoute>
          }
        />
        <Route path="/my-lists" element={<Navigate to="/my-lists/contacts" replace />} />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AccountSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/email/data"
          element={
            <AccessRoute allowed={canAccessEmailWorkspace}>
              <EmailData />
            </AccessRoute>
          }
        />
        <Route
          path="/email/campaign-builder"
          element={
            <AccessRoute allowed={canAccessEmailWorkspace}>
              <EmailCampaignBuilder />
            </AccessRoute>
          }
        />
        <Route
          path="/email/campaign-editor"
          element={
            <AccessRoute allowed={canAccessEmailWorkspace}>
              <EmailCampaignEditor />
            </AccessRoute>
          }
        />
        <Route
          path="/email/email-builder"
          element={
            <AccessRoute allowed={canAccessEmailWorkspace}>
              <EmailBuilder />
            </AccessRoute>
          }
        />
        <Route path="/email" element={<Navigate to="/email/data" replace />} />
        <Route
          path="/command-center"
          element={
            <CommandCenterRoute>
              <CommandCenter />
            </CommandCenterRoute>
          }
        />
        <Route
          path="/payment-results"
          element={
            <AdminRoute>
              <PaymentResults />
            </AdminRoute>
          }
        />
        <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
        <Route path="/checkout" element={<PaymentCheckout />} />
        <Route
          path="/ai-engagement-protocol"
          element={
            <ProtectedRoute>
              <AIEngagementProtocol />
            </ProtectedRoute>
          }
        />
        <Route
          path="/listening-linkedin"
          element={
            <ProtectedRoute>
              <AIEngagementProtocol />
            </ProtectedRoute>
          }
        />
        <Route
          path="/listening-facebook"
          element={
            <ProtectedRoute>
              <AIEngagementProtocol />
            </ProtectedRoute>
          }
        />
        <Route path="/set-password" element={<SetPassword />} />
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/lead-research" element={<Navigate to="/lead-search" replace />} />
        {/* Legacy route redirects */}
        <Route
          path="/intelligence"
          element={<Navigate to="/Recruiting" replace />}
        />
        <Route path="/dashboard" element={<Navigate to="/home" replace />} />
        <Route path="/recruiter-intelligence" element={<Navigate to="/Recruiting" replace />} />
        <Route path="/investor-intelligence" element={<Navigate to="/Investors" replace />} />
        <Route path="/funding-intelligence" element={<Navigate to="/Capital" replace />} />
        <Route path="/ecommerce-intelligence" element={<Navigate to="/Socials" replace />} />
        <Route path="/booking-ai-agent" element={<Navigate to="/podcasts" replace />} />
        <Route path="/recruiting" element={<Navigate to="/Recruiting" replace />} />
        <Route path="/investors" element={<Navigate to="/Investors" replace />} />
        <Route path="/capital" element={<Navigate to="/Capital" replace />} />
        <Route path="/socials" element={<Navigate to="/Socials" replace />} />
        <Route path="/podcast" element={<Navigate to="/podcasts" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
